import { useEffect } from "react";

/**
 * Reveal-on-scroll observer, ported verbatim from the vanilla MAIN-WEBSITE
 * <script> block. A single global observer watches every `.reveal` and
 * `.reveal-group` element and adds the `is-visible` class once it scrolls into
 * view (threshold 0.15, rootMargin "0px 0px -10% 0px"), then unobserves it.
 *
 * `.reveal-group` children get their `--i` stagger index set up-front so
 * steward.css can delay each `.reveal-item` (`transition-delay: calc(var(--i) *
 * var(--stagger))`). prefers-reduced-motion is handled by steward.css (it forces
 * reveal elements visible), so the observer logic is identical for everyone.
 *
 * Mount this once (the integrator does, in the page root). Do NOT add
 * per-component observers.
 */
export function useRevealObserver(): void {
  useEffect(() => {
    document.querySelectorAll<HTMLElement>(".reveal-group").forEach((group) => {
      group
        .querySelectorAll<HTMLElement>(".reveal-item")
        .forEach((item, i) => item.style.setProperty("--i", String(i)));
    });

    const revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = entry.target as HTMLElement;
          target.classList.add("is-visible");
          target.addEventListener(
            "transitionend",
            () => {
              target.style.willChange = "auto";
            },
            { once: true },
          );
          revealObserver.unobserve(target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );

    document
      .querySelectorAll<HTMLElement>(".reveal, .reveal-group")
      .forEach((el) => revealObserver.observe(el));

    return () => revealObserver.disconnect();
  }, []);
}
